import { inject, Injectable, signal } from '@angular/core';
import {
  ICorporatePartner,
  NavSlimLinks
} from '@com-delta-idp/mnx-sitewide-header-ui';
import {
  ApplicationConfig,
  BookWidgetAem,
  ExtendedCookieHelperUtilsService,
  MultiCityRecentSearchService,
  MultiCitySearchCache,
  NavSlimService,
  RetrieveSearch,
  RetrieveSearchService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { Observable } from 'rxjs';
import { HeaderSlimAdapterService } from '../../adapters/header-slim/header-slim-adapter.service';
import { MODIFY_BUTTON } from '../../constants/navigation-links/navigation-linkts-constants';
import { BookingFlightsFacadeService } from '../booking-flights/booking-flights-facade.service';
import { HeaderSlimDeepLinkService } from './header-slim-deep-link.service';

@Injectable()
export abstract class HeaderSlimBaseService {
  cookieHelper = inject(ExtendedCookieHelperUtilsService);
  bookingFlightsFacadeService = inject(BookingFlightsFacadeService);
  appConfig = inject(ApplicationConfig);
  multiCityRecentSearchService = inject(MultiCityRecentSearchService);
  retrieveSearchService = inject(RetrieveSearchService);
  adapter = inject(HeaderSlimAdapterService);
  deepLinkService = inject(HeaderSlimDeepLinkService);
  navSlimService = inject(NavSlimService);

  corpPartner!: ICorporatePartner;
  aem!: BookWidgetAem;
  tripType = signal('');

  navSlimDetails = signal<NavSlimLinks>({
    modifyButton: MODIFY_BUTTON
  } as NavSlimLinks);

  protected getTripType(): string {
    return this.cookieHelper.getTripType()?.toUpperCase() ?? '';
  }

  protected isCookiePresent(): boolean {
    const cookieValue = this.cookieHelper.getCookieValue(
      this.deepLinkService.deepLinkCacheKey
    );
    return cookieValue === this.deepLinkService.deepLinkXssid;
  }

  processSlimNav(
    tripType: string
  ): Observable<MultiCitySearchCache> | Observable<RetrieveSearch[]> {
    if (tripType === 'MULTICITY') {
      return this.getMultiCityData();
    } else {
      return this.retrieveSearchService.getRetrieveSearchContent$();
    }
  }

  getMultiCityData(): Observable<MultiCitySearchCache> {
    const mc_cache_key = this.cookieHelper.getMcCacheKey() ?? '';
    return this.multiCityRecentSearchService.getMultiCityRecentSearches$({
      cacheKey: mc_cache_key
    });
  }

  protected processDeepLinkResponse(
    response: MultiCitySearchCache,
    tripType: string
  ) {
    if (tripType !== 'MULTICITY') {
      this.setDeepLinkSearch(this.adapter.recentSearchData, tripType);
    } else {
      this.handleMultiCityDeepLink(response, tripType);
    }
  }

  protected handleMultiCityDeepLink(
    response: MultiCitySearchCache,
    tripType: string
  ) {
    const updateSearch: MultiCitySearchCache = {
      cacheKey: this.deepLinkService.deepLinkCacheKey,
      value: response.value
    };
    this.navSlimDetails.set(
      this.adapter.getContent(this.aem, updateSearch, tripType)
    );
    this.cookieHelper.setCookies(
      'mc_cache_key',
      this.deepLinkService.deepLinkCacheKey
    );
  }

  setDeepLinkSearch(
    recentSearchValue: MultiCitySearchCache | RetrieveSearch | undefined,
    tripType: string
  ) {
    this.cookieHelper.setCookies('trip_type', tripType);
    if (tripType === 'MULTICITY') {
      return;
    } else {
      this.setRecentSearch(recentSearchValue);
    }
  }

  setRecentSearch(
    recentData: MultiCitySearchCache | RetrieveSearch | undefined
  ) {
    this.deepLinkService.setRecentSearch(
      recentData,
      this.tripType(),
      (response) => {
        this.navSlimDetails.set(
          this.adapter.getContent(this.aem, response, this.tripType())
        );
      }
    );
  }

  checkToCallDeepLinkSlimNav(): boolean {
    const isCookiePresent =
      this.cookieHelper.getCookieValue(
        this.deepLinkService.deepLinkCacheKey
      ) === this.deepLinkService.deepLinkXssid;

    return (
      !isCookiePresent ||
      (isCookiePresent &&
        Object.keys(this.adapter.recentSearchData).length === 0)
    );
  }

  processDeepLinkSlimNav(): boolean {
    return this.deepLinkService.processDeepLinkSlimNav();
  }
}
